// src/pages/contabilidad/FacturacionInbox.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../utils/supabase";
import { loadBillingAttachmentsBatch } from "../../utils/billing-attachments";

const fmtMoney = (n) =>
  new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtDateInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const todayYMD = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const MONTH_OPTIONS = [
  { value: "", label: "Todos los meses" },
  { value: "01", label: "Enero" },
  { value: "02", label: "Febrero" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Mayo" },
  { value: "06", label: "Junio" },
  { value: "07", label: "Julio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

function buildDetailUrl(osId, invoiceId) {
  const base = window.location.origin;
  return `${base}/facturacion?id=${osId}&invoice_id=${invoiceId}`;
}

function formatPoLabel(r) {
  // La vista trae po_base_number (int). Mostramos OC-<base_number>
  if (r?.po_base_number !== null && r?.po_base_number !== undefined && r?.po_base_number !== "") {
    return `OC-${r.po_base_number}`;
  }
  return r?.po_id ? String(r.po_id).slice(0, 8) : "—";
}

function normalizeBillingStatus(status) {
  switch (String(status || "").toLowerCase()) {
    case "requested":
      return "sent";
    case "issued":
      return "invoiced";
    case "closed":
      return "cancelled";
    case "draft":
    case "sent":
    case "invoiced":
    case "cancelled":
      return String(status).toLowerCase();
    default:
      return "draft";
  }
}

function billingStatusLabel(status) {
  switch (normalizeBillingStatus(status)) {
    case "sent":
      return "Enviado para facturar";
    case "invoiced":
      return "Facturado";
    case "cancelled":
      return "Cerrada";
    case "draft":
    default:
      return "Borrador";
  }
}

function isLockedBillingStatus(status) {
  const st = normalizeBillingStatus(status);
  return st === "invoiced" || st === "cancelled";
}

function canAccountantMarkInvoiced(status) {
  return normalizeBillingStatus(status) === "sent";
}

export default function FacturacionInbox() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [me, setMe] = useState(null); // {id, full_name, role}
  const [rows, setRows] = useState([]);

  const [statusFilter, setStatusFilter] = useState("all");
  const [clientSearch, setClientSearch] = useState("");
  const [ccOrOcFilter, setCcOrOcFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [invoiceNumberDrafts, setInvoiceNumberDrafts] = useState({});
  const [invoiceNumberEditableIds, setInvoiceNumberEditableIds] = useState({});
  const [savingInvoiceNumberId, setSavingInvoiceNumberId] = useState(null);

  const filtered = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const ccQ = ccOrOcFilter.trim().toLowerCase();
    const monthQ = monthFilter.trim();

    return (rows || [])
      .filter((r) =>
        statusFilter === "all" ? true : normalizeBillingStatus(r.status) === statusFilter
      )
      .filter((r) => {
        if (!q) return true;

        const blob = [
          r.client_name,
          r.client_cuit,
          r.client_number,
        ]
          .filter(Boolean)
          .join(" | ")
          .toLowerCase();

        return blob.includes(q);
      })
      .filter((r) => {
        if (!ccQ) return true;

        const poLabel = formatPoLabel(r);
        const ccLike = [
          r.cost_center,
          r.oc_number,
          r.accept_reference,
          r.po_base_number,
          poLabel,
          ...(r.oc_documents || []).flatMap((document) => [document?.number, document?.label]),
          ...(r.remito_documents || []).flatMap((document) => [document?.number, document?.label]),
        ]
          .filter(Boolean)
          .join(" | ")
          .toLowerCase();

        return ccLike.includes(ccQ);
      })
      .filter((r) => {
        if (!monthQ) return true;

        const dates = [r.requested_at, r.issued_at, r.created_at]
          .filter(Boolean)
          .map((d) => {
            const iso = fmtDateInput(d);
            return iso ? iso.slice(5, 7) : "";
          });

        return dates.includes(monthQ);
      });
  }, [rows, statusFilter, clientSearch, ccOrOcFilter, monthFilter]);

  async function loadMe() {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error) throw error;
    if (!user) return null;

    const { data: prof, error: pErr } = await supabase
      .from("profiles")
      .select("id, full_name, role")
      .eq("id", user.id)
      .single();

    if (pErr) throw pErr;
    return prof;
  }

  async function loadInvoices() {
    const { data, error } = await supabase
      .from("v_billing_invoices_list")
      .select(
        [
          "invoice_id",
          "po_id",
          "po_base_number",
          "requested_at",
          "invoice_number",
          "issued_at",
          "status",
          "note",
          "created_at",
          "items_count",
          "total_amount",
          "os_id",
          "client_name",
          "client_cuit",
          "client_number",
          "billing_email_1",
          "billing_email_2",
          "oc_number",
          "accept_reference",
          "oc_pdf_url",
        ].join(",")
      )
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async function enrichInvoicesWithAttachments(list) {
    const rowsList = list || [];
    if (!rowsList.length) return rowsList;
    const attachmentsByRow = await loadBillingAttachmentsBatch(rowsList);
    return rowsList.map((row, index) => {
      const attachments = attachmentsByRow[index] || {
        ocDocuments: [],
        remitoDocuments: [],
      };
      return {
        ...row,
        oc_documents: attachments.ocDocuments,
        remito_documents: attachments.remitoDocuments,
        oc_pdf_url: attachments.ocDocuments[0]?.url || row?.oc_pdf_url || null,
        remito_pdf_url: attachments.remitoDocuments[0]?.url || row?.remito_pdf_url || null,
      };
    });
  }

  const refresh = async () => {
    try {
      setLoading(true);
      setErr(null);
      const list = await loadInvoices();
      setRows(list);
      setLoading(false);
      try {
        const enriched = await enrichInvoicesWithAttachments(list);
        setRows(enriched);
      } catch (attachmentError) {
        console.warn("[FacturacionInbox][attachments]", attachmentError);
      }
    } catch (e) {
      console.error("[FacturacionInbox][refresh]", e);
      setErr(e?.message || "No se pudo cargar la bandeja de facturación.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const channel = supabase
      .channel("facturacion-inbox-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "billing_invoices" },
        () => {
          refresh();
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "billing_invoice_items" },
        () => {
          refresh();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const prof = await loadMe();
        if (!alive) return;

        setMe(prof);

        if (!prof) {
          setErr("Necesitás iniciar sesión para ver esta página.");
          setRows([]);
          return;
        }

        if (prof.role !== "accountant") {
          if (prof.role === "admin" || prof.role === "lab_manager") {
            navigate("/facturacion/laboratorio", { replace: true });
            return;
          }
          setErr("No tenés permisos para ver contabilidad / facturación.");
          setRows([]);
          return;
        }

        const list = await loadInvoices();
        if (!alive) return;
        setRows(list);
        setLoading(false);
        try {
          const enriched = await enrichInvoicesWithAttachments(list);
          if (!alive) return;
          setRows(enriched);
        } catch (attachmentError) {
          console.warn("[FacturacionInbox][attachments]", attachmentError);
        }
      } catch (e) {
        console.error("[FacturacionInbox]", e);
        setErr(e?.message || "No se pudo cargar la bandeja de facturación.");
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const countsByStatus = useMemo(() => {
    const map = new Map();
    for (const r of rows || []) {
      const st = normalizeBillingStatus(r.status);
      map.set(st, (map.get(st) || 0) + 1);
    }
    return map;
  }, [rows]);

  const onOpen = (r) => {
    if (!r?.os_id || !r?.invoice_id) {
      alert("Esta factura no tiene OS asociada (probablemente no tiene ítems).");
      return;
    }
    window.open(buildDetailUrl(r.os_id, r.invoice_id), "_blank");
  };

  const onCopyLink = async (r) => {
    if (!r?.os_id || !r?.invoice_id) {
      alert("Esta factura no tiene OS asociada (probablemente no tiene ítems).");
      return;
    }
    const url = buildDetailUrl(r.os_id, r.invoice_id);
    try {
      await navigator.clipboard.writeText(url);
      alert("Link copiado ✅");
    } catch {
      prompt("Copiá el link:", url);
    }
  };

  const updateStatus = async (invoiceId, nextStatus) => {
    if (!invoiceId) return;
    const normalizedStatus = normalizeBillingStatus(nextStatus);
    const currentRow = (rows || []).find((r) => r.invoice_id === invoiceId) || null;
    const currentStatus = normalizeBillingStatus(currentRow?.status);

    if (currentStatus !== "sent" || normalizedStatus !== "invoiced") {
      setErr("Contabilidad solo puede pasar un pedido enviado a estado facturado.");
      return;
    }

    const nextIssuedAt = todayYMD();
    const payload = {
      status: normalizedStatus,
      ...(!currentRow?.issued_at ? { issued_at: nextIssuedAt } : {}),
    };

    // optimista
    setRows((prev) =>
      (prev || []).map((r) =>
        r.invoice_id === invoiceId
          ? {
              ...r,
              status: normalizedStatus,
              ...(payload.issued_at ? { issued_at: payload.issued_at } : {}),
            }
          : r
      )
    );

    try {
      setErr(null);

      const { error } = await supabase
        .from("billing_invoices")
        .update(payload)
        .eq("id", invoiceId);

      if (error) throw error;

      // refresco para traer totales/estado definitivo
      await refresh();
    } catch (e) {
      console.error("[FacturacionInbox][updateStatus]", e);
      setErr(e?.message || "No se pudo actualizar el estado.");
      // rollback: recargar
      await refresh();
    }
  };

  const updateInvoiceNumber = async (invoiceId, nextInvoiceNumber) => {
    if (!invoiceId) return;

    const normalized = String(nextInvoiceNumber || "").trim();
    const nextValue = normalized || null;

    const currentRow = (rows || []).find((r) => r.invoice_id === invoiceId) || null;
    const currentValue = String(currentRow?.invoice_number || "").trim() || null;
    if (currentValue === nextValue) {
      setInvoiceNumberDrafts((prev) => {
        const copy = { ...prev };
        delete copy[invoiceId];
        return copy;
      });
      return;
    }

    setSavingInvoiceNumberId(invoiceId);

    // optimista
    setRows((prev) =>
      (prev || []).map((r) =>
        r.invoice_id === invoiceId ? { ...r, invoice_number: nextValue } : r
      )
    );

    try {
      setErr(null);

      const { error } = await supabase
        .from("billing_invoices")
        .update({ invoice_number: nextValue })
        .eq("id", invoiceId);

      if (error) throw error;

      setInvoiceNumberDrafts((prev) => {
        const copy = { ...prev };
        delete copy[invoiceId];
        return copy;
      });
      setInvoiceNumberEditableIds((prev) => {
        const copy = { ...prev };
        delete copy[invoiceId];
        return copy;
      });
      await refresh();
    } catch (e) {
      console.error("[FacturacionInbox][updateInvoiceNumber]", e);
      setErr(e?.message || "No se pudo actualizar el número de factura.");
      await refresh();
    } finally {
      setSavingInvoiceNumberId(null);
    }
  };

  const enableInvoiceNumberEdit = (r) => {
    if (!r?.invoice_id) return;

    const currentValue = String(r.invoice_number || "").trim() || "sin número";
    const ok = window.confirm(
      `Vas a habilitar la edición del número de factura actual (${currentValue}). Si se cambia por error puede afectar la sincronización con la modal. ¿Querés continuar?`
    );
    if (!ok) return;

    setInvoiceNumberEditableIds((prev) => ({
      ...prev,
      [r.invoice_id]: true,
    }));
    setInvoiceNumberDrafts((prev) => ({
      ...prev,
      [r.invoice_id]: String(r.invoice_number || ""),
    }));
  };

  const enableStatusEdit = (r) => {
    if (!r?.invoice_id) return;

    const currentStatus = billingStatusLabel(r.status);
    if (!canAccountantMarkInvoiced(r.status)) {
      setErr("Ese estado no se edita desde contabilidad.");
      return;
    }
    const ok = window.confirm(
      `Vas a cambiar el estado actual (${currentStatus}) a Facturado. Si lo cambiás por error puede afectar el circuito de facturación. ¿Querés continuar?`
    );
    if (!ok) return;

    updateStatus(r.invoice_id, "invoiced");
  };

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white px-3 py-4 sm:px-6 lg:px-8">
      <div className="max-w-[1900px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <div className="text-xl font-semibold">Contabilidad · Solicitudes de Facturación</div>
            <div className="text-xs text-white/60">
              Sesión: {me?.full_name || "—"} ({me?.role || "—"})
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-sm"
              disabled={loading}
            >
              {loading ? "Cargando…" : "Refrescar"}
            </button>
            <button onClick={logout} className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-500 text-sm">
              Salir
            </button>
          </div>
        </div>

        {err ? (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-900/20 p-3 text-sm text-red-200">
            {err}
          </div>
        ) : null}

        {loading ? (
          <div className="text-sm text-white/70">Cargando…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 mb-5">
              <div className="xl:col-span-3 rounded-lg border border-white/10 bg-white/5 p-5">
                <div className="text-xs text-white/60 mb-1">Estado</div>
                <select
                  className="w-full rounded bg-black/40 border border-white/15 p-3 text-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos ({rows.length})</option>
                  <option value="draft">Borrador ({countsByStatus.get("draft") || 0})</option>
                  <option value="sent">
                    Enviado para facturar ({countsByStatus.get("sent") || 0})
                  </option>
                  <option value="invoiced">
                    Facturado ({countsByStatus.get("invoiced") || 0})
                  </option>
                  <option value="cancelled">
                    Cerrada ({countsByStatus.get("cancelled") || 0})
                  </option>
                </select>
              </div>

              <div className="xl:col-span-9 rounded-lg border border-white/10 bg-white/5 p-5">
                <div className="text-xs text-white/60 mb-1">Buscar por cliente</div>
                <input
                  className="w-full rounded bg-black/40 border border-white/15 p-3 text-sm"
                  placeholder="Cliente, CUIT o número"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />
                <div className="mt-3">
                  <div className="text-xs text-white/60 mb-1">Buscar por CC u OC</div>
                  <input
                    className="w-full rounded bg-black/40 border border-white/15 p-3 text-sm"
                    placeholder="Centro de costo, OC o referencia"
                    value={ccOrOcFilter}
                    onChange={(e) => setCcOrOcFilter(e.target.value)}
                  />
                </div>
                <div className="mt-3">
                  <div className="text-xs text-white/60 mb-1">Buscar por mes</div>
                  <select
                    className="w-full rounded bg-black/40 border border-white/15 p-3 text-sm"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                  >
                    {MONTH_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-white/10 bg-white/5 overflow-x-auto">
              <table className="min-w-[1600px] w-full text-sm">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-4 py-3 whitespace-nowrap text-left font-semibold">Cliente</th>
                    <th className="px-4 py-3 whitespace-nowrap text-left font-semibold">OC/PO</th>
                    <th className="px-4 py-3 whitespace-nowrap text-left font-semibold">Pedido</th>
                    <th className="px-4 py-3 whitespace-nowrap text-left font-semibold">Emisión</th>
                    <th className="px-4 py-3 whitespace-nowrap text-left font-semibold">Nº factura</th>
                    <th className="px-4 py-3 whitespace-nowrap text-left font-semibold">Estado</th>
                    <th className="px-4 py-3 whitespace-nowrap text-right font-semibold">Ítems</th>
                    <th className="px-4 py-3 whitespace-nowrap text-right font-semibold">Total</th>
                    <th className="px-4 py-3 whitespace-nowrap text-left font-semibold min-w-[420px]">Nota</th>
                    <th className="px-4 py-3 whitespace-nowrap text-right font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-6 text-white/60">
                        No hay resultados.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((r) => {
                      const poLabel = formatPoLabel(r);
                      const ocRef = r.oc_number || r.accept_reference || null;
                      const ocDocuments = Array.isArray(r.oc_documents) ? r.oc_documents : [];
                      const remitoDocuments = Array.isArray(r.remito_documents)
                        ? r.remito_documents
                        : [];
                      const emails = [r.billing_email_1, r.billing_email_2].filter(Boolean).join(", ");

                      return (
                        <tr key={r.invoice_id} className="odd:bg-white/0 even:bg-white/5">
                          <td className="px-4 py-3 align-top min-w-[280px]">
                            <div className="font-medium">{r.client_name || "—"}</div>
                            <div className="text-xs text-white/60">
                              {r.client_cuit ? `CUIT: ${r.client_cuit}` : "—"}
                              {r.client_number ? ` · Cliente Nº: ${r.client_number}` : ""}
                            </div>
                            {emails ? <div className="text-xs text-white/50">{emails}</div> : null}
                            <div className="mt-2">
                              <button
                                onClick={() => onOpen(r)}
                                type="button"
                                className="px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-sm"
                                title="Abrir detalle"
                              >
                                Abrir detalle
                              </button>
                            </div>
                          </td>

                          <td className="px-4 py-3 align-top min-w-[220px]">
                            <div className="font-medium">{poLabel}</div>
                            {ocRef ? <div className="text-xs text-white/60">{ocRef}</div> : null}
                            {ocDocuments.length ? (
                              <div className="mt-1 space-y-0.5">
                                {ocDocuments.map((document, index) => (
                                  <div key={document.id || document.url || index}>
                                    <a
                                      className="text-xs text-emerald-300 underline"
                                      href={document.url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {document.label || `Ver OC ${index + 1}`}
                                    </a>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            {remitoDocuments.length ? (
                              <div className="mt-1 space-y-0.5">
                                {remitoDocuments.map((document, index) => (
                                  <div key={document.id || document.url || index}>
                                    <a
                                      className="text-xs text-sky-300 underline"
                                      href={document.url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {document.label || `Ver remito ${index + 1}`}
                                    </a>
                                  </div>
                                ))}
                              </div>
                            ) : r.remito_pdf_url ? (
                              <div>
                                <a
                                  className="text-xs text-sky-300 underline"
                                  href={r.remito_pdf_url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Ver remito
                                </a>
                              </div>
                            ) : null}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.requested_at)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.issued_at)}</td>

                          <td className="px-4 py-3 min-w-[220px]">
                            <div className="flex items-center gap-2">
                              <input
                                className="w-full max-w-[150px] rounded bg-black/40 border border-white/15 px-2 py-1 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                                value={invoiceNumberDrafts[r.invoice_id] ?? r.invoice_number ?? ""}
                                placeholder="Nº factura"
                                disabled={
                                  Boolean(r.invoice_number) &&
                                  !invoiceNumberEditableIds[r.invoice_id]
                                }
                                readOnly={
                                  Boolean(r.invoice_number) &&
                                  !invoiceNumberEditableIds[r.invoice_id]
                                }
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setInvoiceNumberDrafts((prev) => ({
                                    ...prev,
                                    [r.invoice_id]: next,
                                  }));
                                }}
                                onBlur={(e) => {
                                  if (
                                    Boolean(r.invoice_number) &&
                                    !invoiceNumberEditableIds[r.invoice_id]
                                  ) {
                                    return;
                                  }
                                  updateInvoiceNumber(r.invoice_id, e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.currentTarget.blur();
                                  }
                                }}
                              />
                              {Boolean(r.invoice_number) && !invoiceNumberEditableIds[r.invoice_id] ? (
                                <button
                                  type="button"
                                  className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10 text-[10px] whitespace-nowrap"
                                  onClick={() => enableInvoiceNumberEdit(r)}
                                  disabled={savingInvoiceNumberId === r.invoice_id}
                                >
                                  Modificar
                                </button>
                              ) : null}
                              {savingInvoiceNumberId === r.invoice_id ? (
                                <span className="text-[10px] text-white/50 whitespace-nowrap">
                                  Guardando...
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td className="px-4 py-3 min-w-[230px]">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] leading-none px-2 py-1 rounded-full border border-white/15 bg-black/30">
                                {billingStatusLabel(r.status)}
                              </span>
                              {canAccountantMarkInvoiced(r.status) ? (
                                <button
                                  type="button"
                                  className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 border border-emerald-400/40 text-[10px] whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                                  onClick={() => enableStatusEdit(r)}
                                  disabled={savingInvoiceNumberId === r.invoice_id}
                                >
                                  Marcar facturado
                                </button>
                              ) : (
                                <span className="text-[10px] text-white/50 whitespace-nowrap">
                                  Sin acción
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-3 text-right whitespace-nowrap">{r.items_count ?? 0}</td>
                          <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{fmtMoney(r.total_amount)}</td>

                          <td
                            className="px-4 py-3 align-top text-white/80 min-w-[420px] max-w-[560px] whitespace-normal break-words leading-relaxed"
                            title={r.note || ""}
                          >
                            {r.note || ""}
                          </td>

                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button
                              onClick={() => onOpen(r)}
                              type="button"
                              className="px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-sm mr-2"
                              title="Abrir detalle"
                            >
                              Abrir detalle
                            </button>
                            <button
                              onClick={() => onCopyLink(r)}
                              type="button"
                              className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-sm"
                              title="Copiar link"
                            >
                              Copiar link
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-xs text-white/60">
              Nota: “OC/PO” es la Orden de Compra (purchase_orders.base_number). El “Centro de costo” viene del código de OS (OS-6499-xxx) y se ve dentro del detalle.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
